import { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import ProductProfile from './ProductProfile';
import StockControl from './StockControl';
import ExtraDetail from './ExtraDetail';
import BrandDetail from './BrandDetail';
import AuditProfile from './AuditProfile';
import ReplenishmentProfile from './ReplenishmentProfile';
import GLAccountCodes from './GLAccountCodes';
import DetailsNavigation from './DetailsNavigation';
import NewProductsModal from '../../modals/newProductsModals/NewProductsModal';
import { ProductSubmissionData, ProductFormData } from 'features/products/schemas/productSchemas';
import { UpdateProductData } from 'features/products/types/types';
import { transformFormDataToUpdateProductData, transformProductDataToFormData } from 'features/products/utils/productDataMappers';
import { ProductFormProvider, useProductForm } from 'features/products/context/ProductDetailContext';
import { useLazyGetProductDetailsQuery, useUpdateProductMutation, useLazyGetAuditProfileQuery, useDeleteProductMutation } from 'features/products/redux/productApiSlice';
import { setSelectedProduct } from 'features/products/redux/productSlice';
import { useProductSearch } from 'hooks/useProductSearch';
import { toast } from 'react-toastify';
import { useConfirmation } from 'hooks/useConfirmation';
import DeleteModal from 'Components/Common/DeleteModal';
import { ProductConfiguratorModal } from 'features/products/components/configurator';

const DetailsTabContent = ({ justSaved }: { justSaved: boolean }) => {
  // Redux state
  const selectedProduct = useSelector((state: RootState) => state.products.selectedProduct);
  const selectedProductLoading = useSelector((state: RootState) => state.products.selectedProductLoading);

  const { form, resetForm, updateInitialValues } = useProductForm();
  
  // Product search hook for refresh functionality
  const { refreshLastSearchAndAutoSelect, removeDeletedProduct } = useProductSearch();

  //Delete Product API 
  const [deleteProduct] = useDeleteProductMutation();

  // Confirmation modal hook for deletion of products
  const { confirmation, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();


  // Track the current product ID to prevent unnecessary resets
  const [currentProductId, setCurrentProductId] = useState<number | undefined>(undefined);
  const [showConfigurator, setShowConfigurator] = useState(false);

  // Sync Redux data with form - only reset when product changes
  useEffect(() => {
    if (selectedProduct) {
      const productId = selectedProduct.rowId;
      // Reset form if switching to different product OR just completed a save
      if (currentProductId !== productId || justSaved) {
        const formData = transformProductDataToFormData(selectedProduct);
        form.reset(formData);
        updateInitialValues(formData); // Update initial values for dirty tracking
        setCurrentProductId(productId);

        // justSaved flag will be cleared by parent component
      } else {
        // console.log('Same product, not resetting form to preserve user changes');
      }
    } else {
      // No product selected, reset to empty form
      if (currentProductId !== undefined) {
        resetForm();
        setCurrentProductId(undefined);
      }
    }
  }, [selectedProduct, form, resetForm, updateInitialValues, currentProductId, justSaved]);

  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [showAuditProfile, setShowAuditProfile] = useState(false);

  const handleNew = () => {
    setShowNewProductModal(true);
  };


  const handleCancel = () => {
    console.log('Cancelling changes...');
  };

  const handleDelete = () => {
    if (!selectedProduct?.rowId) {
      toast.error('Please select a product to delete');
      return;
    }

    showConfirmation({
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product? This action cannot be undone.',
      recordName: 'product',
      recordId: selectedProduct?.rowId?.toString(),
      deleteButtonText: 'Yes, Delete Product',
      onConfirm: performDelete
    });

  }


  //Perform delete function with API call
  const performDelete = async () => {
    if (!selectedProduct?.rowId) {
      toast.error('No product selected');
      return;
    }
    try {
      const productRowId = selectedProduct.rowId;
      await deleteProduct({ productRowId }).unwrap();
      toast.success('Product deleted successfully');
      
      // Remove the deleted product from the current search results
      removeDeletedProduct(productRowId);
      
      // Reset form
      resetForm();
      // Reset current product ID
      setCurrentProductId(undefined);
      
      // Refresh the search results and auto-select first item
      await refreshLastSearchAndAutoSelect();
      
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleCopy = () => {
    console.log('Copying product...');
  };

  const handleLoadImage = () => {
    console.log('Loading product image...');
  };

  const handleViewImage = () => {
    console.log('Viewing product image...');
  };

  const handleScanBarcode = () => {
    console.log('Opening barcode scanner...');
  };

  const handleGenerateBarcode = () => {
    console.log('Generating barcode...');
  };

  // Open product configurator modal
  const handleConfigurator = () => {
    setShowConfigurator(true);
  };

  const handleTaskManager = () => {
    console.log('Opening task manager...');
  };

  const handleSingleBundle = () => {
    console.log('Opening single bundle...');
  };

  const handleProcessProduct = () => {
    console.log('Processing product...');
  };

  // Show loading state
  if (selectedProductLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="details-tab">
      <DetailsNavigation
        onNew={handleNew}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onCopy={handleCopy}
        onLoadImage={handleLoadImage}
        onViewImage={handleViewImage}
        onScanBarcode={handleScanBarcode}
        onGenerateBarcode={handleGenerateBarcode}
        onConfigurator={handleConfigurator}
        onTaskManager={handleTaskManager}
        onSingleBundle={handleSingleBundle}
        onProcessProduct={handleProcessProduct}
        showAuditProfile={showAuditProfile}
        setShowAuditProfile={setShowAuditProfile}
      />

      <Row className="g-3">
        {/* Main Product Information */}
        <Col sm={6}>
          <ProductProfile />
        </Col>

        {/* Stock Control */}
        <Col sm={2}>
          <StockControl />
          <BrandDetail />
        </Col>

        {/* Replenishment Profile */}
        <Col sm={2}>
          <ReplenishmentProfile />
          <GLAccountCodes />
        </Col>

        {/* Extra Details and Audit Profile */}
        <Col sm={2}>
          <ExtraDetail />
          {/* Audit Profile */}
          {showAuditProfile && (
            <AuditProfile />
          )}
        </Col>
      </Row>

      <NewProductsModal
        isOpen={showNewProductModal}
        onClose={() => setShowNewProductModal(false)}
      />

      {/* Delete Confirmation Modal */}
      {confirmation && (
        <DeleteModal
          show={confirmation.isOpen}
          onDeleteClick={handleConfirm}
          onCloseClick={hideConfirmation}
          title={confirmation.title}
          message={confirmation.message}
          recordName={confirmation.recordName}
          recordId={confirmation.recordId}
          deleteButtonText={confirmation.deleteButtonText}
          isLoading={confirmation.isLoading}
        />
      )}

      {showConfigurator && (
        <ProductConfiguratorModal
          isOpen={showConfigurator}
          onClose={() => setShowConfigurator(false)}
          productRowId={selectedProduct?.parRowId}
        />
      )}
    </div>
  );
};

const DetailsTab = () => {
  const dispatch = useDispatch();
  const [updateProductApi] = useUpdateProductMutation();
  const [getProductDetails, { isLoading: isGettingProductDetails }] = useLazyGetProductDetailsQuery();
  const [getAuditProfile] = useLazyGetAuditProfileQuery();

  const selectedProduct = useSelector((state: RootState) => state.products.selectedProduct);

  // Move justSaved state to parent component so it can be accessed by handleFormSubmit
  const [justSaved, setJustSaved] = useState(false);

  // Clear justSaved flag after form reset has been processed
  useEffect(() => {
    if (justSaved) {
      const timer = setTimeout(() => {
        setJustSaved(false);
      }, 100); // Small delay to ensure form reset is processed

      return () => clearTimeout(timer);
    }
  }, [justSaved]);

  const handleFormSubmit = async (data: ProductSubmissionData, resetFormWithNewValues?: (values: Partial<ProductFormData>) => void) => {
    try {

      // Transform form data to API format
      const apiData = transformFormDataToUpdateProductData(data);

      // console.log('apiData.asUpdatedProductData', apiData as UpdateProductData);
      apiData.nextAvlDate = null;
      apiData.location = '1';

      // Check if the product has been modified by another user
      if (selectedProduct?.rowId) {
        const auditProfile = await getAuditProfile({ productRowId: selectedProduct.rowId }).unwrap();

        // Compare the updated times
        if (selectedProduct.updated !== auditProfile.data.updated) {

          const productDetails = await getProductDetails({ productRowId: selectedProduct.rowId });
          if (productDetails?.data?.data?.[0]) {
            // Dispatch to existing product slice
            dispatch(setSelectedProduct(productDetails?.data?.data[0]));
          } else {
            toast.warn('No product details found in response');
          }
          console.error('Product has been modified by another user. Cannot save changes.');
          toast.error('This product has been modified by another user since you loaded it. Please try again.');
          // Set flag to trigger form reset with fresh data
          setJustSaved(true);
          return;
        }
      }

      // Call the update product API
      await updateProductApi(apiData as UpdateProductData);

      toast.success('Product updated successfully');

      const productDetails = await getProductDetails({ productRowId: apiData.rowId });
      if (productDetails?.data?.data?.[0]) {
        // Dispatch to existing product slice
        dispatch(setSelectedProduct(productDetails?.data?.data[0]));

        // Set flag to trigger form reset with fresh data
        setJustSaved(true);

        // Reset form with the updated product data to prevent dirty state issues
        if (resetFormWithNewValues) {
          const updatedFormData = transformProductDataToFormData(productDetails.data.data[0]);
          resetFormWithNewValues(updatedFormData);
        }
      } else {
        toast.warn('No product details found in response');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Form submission error Please try again.');
    }
  };

  return (
    <ProductFormProvider
      onSubmit={handleFormSubmit}
      onError={(error) => console.error('Form error:', error)}
    // isSubmitting={isUpdating}
    >
      {isGettingProductDetails ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <DetailsTabContent justSaved={justSaved} />
      )}
    </ProductFormProvider>
  );
};

export default DetailsTab; 